import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getTicketStatus } from 'src/services/client';

export interface TicketStatus {
  id: string;
  name: string;
  description: string;
  customLabel: string | null;
}

export interface UseTicketStatusesReturn {
  ticketStatuses: TicketStatus[];
  loading: boolean;
  updateCustomLabel: (statusId: string, customLabel: string | null) => void;
}

const GENERIC_LOAD_ERROR_MESSAGE: string =
  'Could not load ticket statuses. Some actions may be unavailable.';

export const useTicketStatuses = (): UseTicketStatusesReturn => {
  const [ticketStatuses, setTicketStatuses] = useState<TicketStatus[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const loadTicketStatuses = async (): Promise<void> => {
    try {
      const response: any = await getTicketStatus();

      // TODO: hotfix — apiReq returns { isError: true } on auth failure
      // instead of a Response, which makes the catch fire a toast during
      // stale-auth boots / session timeout. See serviceRequestDashboard.tsx
      // for the canonical TODO; long-term fix is a single useAuth provider.
      // The 401 arm covers the stale-but-present token case, where apiReq
      // hands back the raw 401 Response on the login page instead of isError.
      if (response?.isError || response?.status === 401) {
        console.warn(
          '[loadTicketStatuses] auth failure loading ticket statuses; suppressing toast (auth lifecycle race — see TODO).',
          response
        );
        return;
      }

      if (!response.ok && response.status !== 200) {
        throw new Error('Failed to load ticket statuses');
      }

      const data: TicketStatus[] = await response.json();

      setTicketStatuses(data ?? []);
    } catch (error: any) {
      console.error('Error loading ticket statuses:', error);
      toast.error('🚫 ' + GENERIC_LOAD_ERROR_MESSAGE, {
        autoClose: 8000,
        position: 'top-center',
      });
    }
  };

  useEffect(() => {
    let active: boolean = true;

    const runInitialLoad = async (): Promise<void> => {
      await loadTicketStatuses();

      if (active) {
        setLoading(false);
      }
    };

    runInitialLoad();

    return () => {
      active = false;
    };
  }, []);

  // legacy/workaround: GET TicketStatus has no stable ORDER BY, so re-fetching
  // after a mutation reorders the list. Update the changed row in place instead.
  const updateCustomLabel = (
    statusId: string,
    customLabel: string | null
  ): void => {
    setTicketStatuses((prev: TicketStatus[]) =>
      prev.map((status: TicketStatus) =>
        status.id === statusId ? { ...status, customLabel } : status
      )
    );
  };

  return {
    ticketStatuses,
    loading,
    updateCustomLabel,
  };
};
